import {Dispatch} from '@reduxjs/toolkit';
import navConstants from '../constants/navConstants';
import {logout} from '../api/Authentication';
import config from '../config';

let leavingApp = false;
let maintenanceStatusInFlight: Promise<boolean> | null = null;

export const isMaintenanceLeaveInProgress = (): boolean => leavingApp;

export const getPlatformMaintenanceStatusUrl = (): string => {
  const apiBase = String(config.API_BASE_URL || '');
  if (/^https?:\/\//i.test(apiBase)) {
    return new URL('/maintenance', apiBase).toString();
  }
  return '/maintenance';
};

/**
 * Reads the gateway's public maintenance flag. This endpoint stays available
 * during platform maintenance; a failed read is treated as "not in maintenance"
 * so a single downstream 503 cannot log the user out.
 */
export const isPlatformMaintenanceEnabled = async (): Promise<boolean> => {
  if (maintenanceStatusInFlight) {
    return maintenanceStatusInFlight;
  }

  maintenanceStatusInFlight = (async () => {
    try {
      const response = await fetch(getPlatformMaintenanceStatusUrl(), {
        method: 'GET',
        credentials: 'include',
        headers: {Accept: 'application/json'},
      });
      if (!response.ok) {
        return false;
      }
      const body = await response.json();
      return Boolean(body?.maintenanceMode);
    } catch {
      return false;
    }
  })().finally(() => {
    maintenanceStatusInFlight = null;
  });

  return maintenanceStatusInFlight;
};

export const leaveAppIfPlatformMaintenance = async (
  dispatch?: Dispatch
): Promise<boolean> => {
  if (leavingApp) {
    return true;
  }

  const enabled = await isPlatformMaintenanceEnabled();
  if (!enabled) {
    return false;
  }

  await leaveAppForMaintenance(dispatch);
  return true;
};

export const leaveAppForMaintenance = async (dispatch?: Dispatch): Promise<void> => {
  if (leavingApp) {
    return;
  }

  if (window.location.pathname === navConstants.auth.unavailable) {
    return;
  }

  leavingApp = true;
  try {
    await logout(dispatch);
  } catch {
    // Cookie sign-out is best-effort; still leave the authenticated app.
  } finally {
    window.location.replace(navConstants.auth.unavailable);
  }
};
